import { el, clear } from "../../utils/dom";
import { getState, setState } from "../../state/store";
import { errorToast, toast } from "./Toasts";
import { generateSeed } from "../../logic/random";
import { DEFAULT_WEIGHTS } from "../../logic/scoring";
import type { ScoringWeights } from "../../types/models";
import { createMultiKnobSlider } from "./MultiKnobSlider";

export function openSettingsDialog() {
    const s = getState();
    const cls = s.classes.find(c => c.id === s.activeClassId);
    if (!cls) return;

    // Create modal backdrop
    const backdrop = el("div", {
        style: "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center;"
    });

    // Create dialog
    const dialog = el("div", {
        style: "background: var(--card); border-radius: 16px; padding: 24px; max-width: 500px; width: 90%; max-height: 80vh; overflow: auto; border: 1px solid var(--border);"
    });

    const title = el("h2", { style: "margin: 0 0 20px 0;" }, "Settings");

    function current(ns: any) { return ns.classes.find((c: any) => c.id === ns.activeClassId)!; }

    // Target size
    const target = el("input", { type: "number", min: "2", max: "10", value: String(cls.settings.targetSize), style: "width: 80px;" }) as HTMLInputElement;
    target.addEventListener("input", () => setState(ns => { current(ns).settings.targetSize = Number(target.value) || 4; }));

    // Range
    const min = el("input", { type: "number", min: "2", max: "10", value: String(cls.settings.minSize), style: "width: 80px;" }) as HTMLInputElement;
    const max = el("input", { type: "number", min: "2", max: "10", value: String(cls.settings.maxSize), style: "width: 80px;" }) as HTMLInputElement;
    min.addEventListener("input", () => setState(ns => { current(ns).settings.minSize = Number(min.value) || 2; }));
    max.addEventListener("input", () => setState(ns => { current(ns).settings.maxSize = Number(max.value) || 4; }));

    // Max groups
    const maxGroupsInput = el("input", {
        type: "number",
        min: "1",
        max: "20",
        value: cls.settings.maxGroups ? String(cls.settings.maxGroups) : "",
        placeholder: "Unlimited",
        style: "width: 80px;"
    }) as HTMLInputElement;
    maxGroupsInput.addEventListener("input", () => {
        setState(ns => {
            const val = Number(maxGroupsInput.value);
            current(ns).settings.maxGroups = val > 0 ? val : undefined;
        });
    });

    // Balance skill toggle
    const balanceSkillToggle = el("label", { class: "toggle-switch" },
        el("input", { type: "checkbox", checked: cls.settings.balanceSkill ? true : undefined,
            onchange: (e: any) => setState(ns => { current(ns).settings.balanceSkill = !!e.target.checked; })
        }),
        el("span", { class: "toggle-slider" })
    );

    // Seed input for reproducible randomness
    const seedInput = el("input", {
        type: "text",
        value: cls.settings.seed ?? "",
        placeholder: "Random each time",
        style: "width: 120px; font-family: monospace;"
    }) as HTMLInputElement;
    seedInput.addEventListener("input", () => {
        setState(ns => {
            const val = seedInput.value.trim();
            current(ns).settings.seed = val || undefined;
        });
    });

    const randomSeedBtn = el("button", {
        style: "padding: 6px 10px; font-size: 12px;",
        title: "Generate random seed"
    }, "🎲");
    randomSeedBtn.addEventListener("click", () => {
        const newSeed = generateSeed();
        seedInput.value = newSeed;
        setState(ns => { current(ns).settings.seed = newSeed; });
        toast("Seed generated: " + newSeed);
    });

    const clearSeedBtn = el("button", {
        style: "padding: 6px 10px; font-size: 12px;",
        title: "Clear seed (random each time)"
    }, "×");
    clearSeedBtn.addEventListener("click", () => {
        seedInput.value = "";
        setState(ns => { current(ns).settings.seed = undefined; });
    });

    const seedControls = el("div", { style: "display: flex; gap: 4px; align-items: center;" },
        seedInput, randomSeedBtn, clearSeedBtn
    );

    const formRow = (label: string, ...controls: HTMLElement[]) =>
        el("div", { style: "display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; gap: 12px;" },
            el("label", { style: "font-weight: 500; flex: 1;" }, label),
            ...controls
        );

    // Scoring Weights Section
    const scoringWeightsSection = el("div", {
        style: "margin-top: 20px; padding-top: 20px; border-top: 1px solid var(--border);"
    });

    const weightsTitle = el("h3", { style: "margin: 0 0 8px 0;" }, "Algorithm Weights");
    const weightsHelp = el("p", {
        style: "margin: 0 0 16px 0; font-size: 12px; color: var(--muted);"
    }, "Drag the handles to adjust how the algorithm prioritizes different factors. Total always equals 100%.");

    const currentWeights = cls.settings.scoringWeights ?? { ...DEFAULT_WEIGHTS };

    // Create multi-knob slider for weights
    const weightsSlider = createMultiKnobSlider({
        segments: [
            { key: "size", label: "Size", color: "#4f46e5", value: currentWeights.size },
            { key: "skill", label: "Skill", color: "#7c3aed", value: currentWeights.skill },
            { key: "tags", label: "Tags", color: "#db2777", value: currentWeights.tags }
        ],
        onChange: (values) => {
            setState(ns => {
                const c = current(ns);
                c.settings.scoringWeights = {
                    size: values.size ?? DEFAULT_WEIGHTS.size,
                    skill: values.skill ?? DEFAULT_WEIGHTS.skill,
                    tags: values.tags ?? DEFAULT_WEIGHTS.tags
                };
            });
        },
        height: 36
    });

    const resetWeightsBtn = el("button", {
        style: "font-size: 12px; padding: 6px 12px; margin-top: 12px;"
    }, "Reset to Defaults");
    resetWeightsBtn.addEventListener("click", () => {
        setState(ns => {
            current(ns).settings.scoringWeights = { ...DEFAULT_WEIGHTS };
        });
        // Update the multi-knob slider
        (weightsSlider as any).setValues({
            size: DEFAULT_WEIGHTS.size,
            skill: DEFAULT_WEIGHTS.skill,
            tags: DEFAULT_WEIGHTS.tags
        });
        toast("Weights reset to defaults");
    });

    scoringWeightsSection.append(weightsTitle, weightsHelp, weightsSlider, resetWeightsBtn);

    // Tag Behavior Section (NEW)
    const tagBehaviorSection = el("div", {
        style: "margin-top: 20px; padding-top: 20px; border-top: 1px solid var(--border);"
    });

    const tagTitle = el("h3", { style: "margin: 0 0 8px 0;" }, "Tag Behavior");
    const tagHelp = el("p", {
        style: "margin: 0 0 12px 0; font-size: 12px; color: var(--muted);"
    }, "Configure how each tag affects group generation.");

    const tagList = el("div", { style: "display: flex; flex-direction: column; gap: 8px;" });

    // Tag creation row
    const tagCreationRow = el("div", {
        style: "display: flex; gap: 8px; margin-bottom: 12px;"
    });

    const newTagInput = el("input", {
        type: "text",
        placeholder: "New tag name...",
        style: "flex: 1;"
    }) as HTMLInputElement;

    const addTagBtn = el("button", { class: "primary" }, "+ Add Tag");

    addTagBtn.addEventListener("click", () => {
        const tagName = newTagInput.value.trim();
        if (!tagName) return;

        const currentState = getState();
        const currentCls = currentState.classes.find(c => c.id === cls.id);
        if (!currentCls) return;

        // Check for duplicates (case-insensitive)
        const exists = currentCls.settings.tags.some(
            t => t.toLowerCase() === tagName.toLowerCase()
        );
        if (exists) {
            errorToast("Tag already exists!");
            return;
        }

        setState(ns => {
            const c = ns.classes.find(x => x.id === cls.id)!;
            c.settings.tags.push(tagName);
            c.settings.tagBehavior.set(tagName, 'balance');
        });

        newTagInput.value = "";
        renderTagBehaviors();
    });

    tagCreationRow.append(newTagInput, addTagBtn);

    function renderTagBehaviors() {
        clear(tagList);

        const currentState = getState();
        const currentCls = currentState.classes.find(c => c.id === currentState.activeClassId);
        if (!currentCls) return;

        // Read tags from master list
        const allTags = [...currentCls.settings.tags].sort();

        if (allTags.length === 0) {
            tagList.appendChild(
                el("p", { style: "color: var(--muted); font-style: italic; text-align: center;" },
                "No tags yet. Create tags above.")
            );
            return;
        }

        for (const tag of allTags) {
            const mode = currentCls.settings.tagBehavior.get(tag) ?? 'balance';

            const row = el("div", { style: "display: flex; gap: 12px; align-items: center;" });

            const tagBadge = el("span", {
                style: "background: var(--bg); padding: 4px 12px; border-radius: 12px; font-weight: 500; flex: 1;"
            }, tag);

            const select = el("select", { style: "flex: 1;" },
                el("option", { value: "balance", selected: mode === 'balance' ? true : undefined }, "Balance across groups"),
                el("option", { value: "group", selected: mode === 'group' ? true : undefined }, "Group together")
            ) as HTMLSelectElement;

            select.addEventListener("change", () => {
                setState(ns => {
                    const c = current(ns);
                    c.settings.tagBehavior.set(tag, select.value as 'balance' | 'group');
                });
            });

            // Delete button
            const deleteBtn = el("button", {
                class: "danger",
                style: "padding: 4px 8px; font-size: 12px;"
            }, "×");

            deleteBtn.addEventListener("click", () => {
                const currentState = getState();
                const currentCls = currentState.classes.find(c => c.id === currentState.activeClassId);
                if (!currentCls) return;

                const inUse = currentCls.students.some(s => s.tags.includes(tag));
                const confirmMsg = inUse
                    ? `Tag "${tag}" is used by students. Remove anyway?`
                    : `Remove tag "${tag}"?`;

                if (!confirm(confirmMsg)) return;

                setState(ns => {
                    const c = ns.classes.find(x => x.id === currentCls.id)!;
                    // Remove from master list
                    c.settings.tags = c.settings.tags.filter(t => t !== tag);
                    // Remove from behavior map
                    c.settings.tagBehavior.delete(tag);
                    // Remove from all students
                    for (const student of c.students) {
                        student.tags = student.tags.filter(t => t !== tag);
                    }
                });

                renderTagBehaviors();
            });

            row.append(tagBadge, select, deleteBtn);
            tagList.appendChild(row);
        }
    }

    renderTagBehaviors();

    tagBehaviorSection.append(tagTitle, tagHelp, tagCreationRow, tagList);

    // Close button
    const closeBtn = el("button", { class: "primary", style: "width: 100%; margin-top: 20px;" }, "Close");
    closeBtn.addEventListener("click", () => document.body.removeChild(backdrop));

    // Danger Zone section
    const dangerZone = el("div", {
        style: "margin-top: 20px; padding-top: 20px; border-top: 1px solid var(--border);"
    });

    const dangerTitle = el("h3", {
        style: "margin: 0 0 8px 0; color: var(--danger);"
    }, "Danger Zone");

    const dangerHelp = el("p", {
        style: "margin: 0 0 12px 0; font-size: 12px; color: var(--muted);"
    }, "Reset will clear all data and reload the application.");

    const resetBtn = el("button", {
        class: "danger",
        style: "width: 100%;"
    }, "Reset App");

    resetBtn.addEventListener("click", () => {
        if (confirm("Are you sure? This will delete all classes and settings.")) {
            console.warn("🗑️ Reset requested - clearing localStorage");
            localStorage.removeItem("GG:v1:state");
            console.info("Reloading application...");
            location.reload();
        }
    });

    dangerZone.append(dangerTitle, dangerHelp, resetBtn);

    const helpText = el("p", {
        style: "margin: 0 0 16px 0; padding: 12px; background: var(--bg); border-radius: 8px; font-size: 13px; color: var(--muted); border: 1px solid var(--border);"
    }, "The generator aims for the target size within the min-max range. Leftover students form one smaller group. Set max groups to limit total number of groups.");

    dialog.append(
        title,
        helpText,
        formRow("Target Group Size", target),
        formRow("Allowed Range (Min - Max)", min, el("span", {}, "—"), max),
        formRow("Max Groups (optional)", maxGroupsInput),
        formRow("Balance Skill Levels", balanceSkillToggle),
        formRow("Seed (reproducible)", seedControls),
        scoringWeightsSection,
        tagBehaviorSection,
        dangerZone,
        closeBtn
    );

    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);

    // Close on backdrop click
    backdrop.addEventListener("click", (e) => {
        if (e.target === backdrop) document.body.removeChild(backdrop);
    });
}
